import { Tabs, Tab, Box, Stack, useTheme, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';

import ButtonComponent from '../button/ButtonComponent';
import { colors } from '../theme/theme';

interface TabItem {
    label?: string;
    content?: React.ReactNode;
    sx?: Record<string, unknown>;
}

interface CommonTabsProps {
    tabs: TabItem[];
    defaultActiveIndex?: number;
    /** When provided, the tab selection is controlled by the parent (use with onTabChange) */
    activeIndex?: number;
    onTabChange?: (index: number) => void;
    tabPosition?: 'top' | 'bottom' | 'left' | 'right';
    primaryBtnContent?: string;
    primaryBtnIcon?: React.ReactNode;
    secondaryBtnContent?: string;
    onPrimaryBtnClick?: () => void;
    onSecondaryBtnClick?: () => void;
    disabled?: boolean;
    isAmortizationScheduleTab?: boolean;
    /** Renders in the header row to the left of primary/secondary buttons (e.g. search) */
    trailingContent?: React.ReactNode;
}

const CommonTabs: React.FC<CommonTabsProps> = ({
    tabs,
    defaultActiveIndex = 0,
    activeIndex: controlledActiveIndex,
    onTabChange,
    tabPosition = 'top',
    primaryBtnContent,
    primaryBtnIcon,
    secondaryBtnContent,
    onPrimaryBtnClick,
    onSecondaryBtnClick,
    disabled = false,
    isAmortizationScheduleTab,
    trailingContent,
}) => {
    const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveIndex);
    const isControlled = controlledActiveIndex !== undefined && controlledActiveIndex !== null;
    const activeTab = isControlled ? controlledActiveIndex : internalActiveTab;

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        // Prevent tab changes if disabled
        if (disabled) {
            return;
        }
        if (!isControlled) {
            setInternalActiveTab(newValue);
        }
        if (onTabChange) onTabChange(newValue);
    };

    const theme = useTheme();
    const isDesktopRow = useMediaQuery(theme.breakpoints.up('lg'));
    const isNarrowMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            sx={{
                display: tabPosition === 'left' || tabPosition === 'right' ? 'flex' : 'block',
            }}
        >
            {/* Tabs Header */}
            <Stack
                direction={isDesktopRow ? 'row' : 'column-reverse'}
                alignItems="center"
                justifyContent="space-between"
                gap={isDesktopRow ? 0 : 2}
                sx={{ borderBottom: `1px solid ${colors.greyPreset[30]}`, flexWrap: 'wrap' }}
                className="tabHeader"
            >
                <Tabs
                    value={activeTab}
                    onChange={handleChange}
                    orientation={
                        tabPosition === 'left' ? 'vertical' : tabPosition === 'right' ? 'vertical' : 'horizontal'
                    }
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        borderRight: tabPosition === 'left' || tabPosition === 'right' ? 1 : 0,
                        borderColor: 'divider',
                        minWidth: tabPosition === 'left' || tabPosition === 'right' ? 150 : 'auto',
                        maxWidth: '100%',
                        opacity: disabled ? 0.5 : 1,
                        pointerEvents: disabled ? 'none' : 'auto',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        '& .MuiTabs-list': {
                            gap: 1.5,
                        },
                        '& .MuiTabs-scrollButtons': {
                            transition: 'width 0.2s ease, opacity 0.2s ease',
                            '&.Mui-disabled': {
                                width: 0,
                                opacity: 0,
                            },
                        },
                        '& .MuiButtonBase-root': {
                            ...theme.typography.body2,
                            minWidth: 'unset',
                            p: '12px 0px',
                            mx: 1.5,
                            lineHeight: '21px',
                            color: theme.palette.text.secondary,
                            '&.Mui-selected': {
                                color: colors.primaryLight[100],
                            },
                        },
                        '& .MuiTabs-indicator': {
                            bgcolor: colors.primaryLight[100],
                        },
                    }}
                >
                    {tabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} sx={tab.sx} tabIndex={0} />
                    ))}
                </Tabs>

                {(primaryBtnContent || secondaryBtnContent || trailingContent) && (
                    <Stack
                        direction={isNarrowMobile ? 'column' : 'row'}
                        alignItems={isNarrowMobile ? 'flex-end' : 'center'}
                        flexWrap="wrap"
                        gap={2}
                        width={isDesktopRow ? 'auto' : '100%'}
                        justifyContent={'end'}
                    >
                        {trailingContent}
                        {primaryBtnContent &&
                            (isAmortizationScheduleTab ? (
                                <ButtonComponent
                                    variant="outlined"
                                    onClick={onPrimaryBtnClick}
                                    endIcon={primaryBtnIcon}
                                    MDButton={true}
                                    fullWidth={false}
                                    sx={{ width: 'fit-content', flexShrink: 0, whiteSpace: 'nowrap' }}
                                >
                                    {primaryBtnContent}
                                </ButtonComponent>
                            ) : (
                                <ButtonComponent
                                    variant="outlined"
                                    onClick={onPrimaryBtnClick}
                                    startIcon={primaryBtnIcon}
                                    MDButton={true}
                                    fullWidth={false}
                                    sx={{ width: 'fit-content', flexShrink: 0, whiteSpace: 'nowrap' }}
                                >
                                    {primaryBtnContent}
                                </ButtonComponent>
                            ))}
                        {secondaryBtnContent && (
                            <ButtonComponent
                                variant="contained"
                                onClick={onSecondaryBtnClick}
                                MDButton={true}
                                fullWidth={false}
                                sx={{ width: 'fit-content', flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                {secondaryBtnContent}
                            </ButtonComponent>
                        )}
                    </Stack>
                )}
            </Stack>
            {/* Tab Content */}
            <Stack pt={2} tabIndex={-1}>
                {tabs[activeTab]?.content}
            </Stack>
        </Box>
    );
};

export default CommonTabs;
